/* eslint-disable jsx-a11y/alt-text */
import { CloudinaryImage as Image } from '@cloudinary/url-gen/assets/CloudinaryImage'
import React from 'react'

import { FEATURE_CLOUDFLARE_IMAGE_OPTIMIZATION } from '@/core/constants/features'
import useFeatures from '@/core/utils/features/useFeatures'

import { CloudflareImageOptions, getCloudflareImageUrl } from './cloudflare'
import { dprSourceSet } from './dprSourceSet'

export type CloudinaryElementProps = {
  image: Image
  quality?: string | number
  format?: string
  options?: CloudflareImageOptions & {
    id: string
  }
}

export type CloudinaryImageProps = CloudinaryElementProps &
  React.ImgHTMLAttributes<HTMLImageElement> & {
    htmlWidth?: number | string
    htmlHeight?: number | string
  }

/**
 * @deprecated This component is deprecated and should not be used in new code.
 * Use {@link CloudImage} from `@/cloud-image/CloudImage` instead.
 *
 * @see {@link https://cloudinary.com/documentation/javascript_integration|Cloudinary JavaScript SDK}
 * @see {@link https://cloudinary.com/documentation/javascript_image_transformations|JavaScript SDK image transformations}
 *
 *
 * @example
 * import { fill } from '@cloudinary/url-gen/actions/resize'
 * import { CloudinaryImage } from '@/cloudinary/CloudinaryImage'
 * import { cloudinary } from '@/cloudinary/cloudinary'
 *
 * <CloudinaryImage
 *   width="90px"
 *   image={cloudinary
 *     .image('system/arrow')
 *     .resize(fill().width(90))}
 * />
 *
 * @example
 * import { Image } from '@masterplandev/ui'
 * import { fill } from '@cloudinary/url-gen/actions/resize'
 * import { CloudinaryImage } from '@/cloudinary/CloudinaryImage'
 * import { cloudinary } from '@/cloudinary/cloudinary'
 *
 * <Image
 *   as={CloudinaryImage}
 *   image={cloudinary
 *     .image('system/invite-user.jpg')
 *     .resize(fill().width(518))}
 *   objectFit="cover"
 * />
 *
 */
export function CloudinaryImage({
  image,
  quality = 'auto:eco',
  format = 'auto',
  options,
  ...props
}: CloudinaryImageProps) {
  const hasCloudflareImageOptimizationEnabled = useFeatures(
    FEATURE_CLOUDFLARE_IMAGE_OPTIMIZATION,
  )

  image.quality(quality).format(format)

  // use htmlWidth, htmlHeight from <Image /> component from ui package
  const { htmlWidth, htmlHeight, ...imageProps } = props

  const src =
    options && hasCloudflareImageOptimizationEnabled
      ? getCloudflareImageUrl(options.id, options)
      : image.toURL()

  const srcSet =
    options && hasCloudflareImageOptimizationEnabled
      ? `${getCloudflareImageUrl(options.id, { dpr: 2, ...options })} 2x`
      : dprSourceSet(image)

  return (
    <img
      width={htmlWidth}
      height={htmlHeight}
      {...imageProps}
      src={src}
      srcSet={srcSet}
    />
  )
}

type CloudinarySourceProps = CloudinaryElementProps &
  React.SourceHTMLAttributes<HTMLSourceElement>

/**
 * @deprecated This component is deprecated and should not be used in new code.
 * Use {@link CloudImage} from `@/cloud-image/CloudImage` instead.
 */
export function CloudinarySource({
  image,
  quality = 'auto:eco',
  format = 'auto',
  ...sourceAttributes
}: CloudinarySourceProps) {
  image.quality(quality).format(format)

  return <source srcSet={dprSourceSet(image)} {...sourceAttributes} />
}
